import { paths } from '@common/types';
import { Box, Button, Divider, Typography } from '@mui/material';
import { t } from 'i18next';
import { MFAMethod, usePrivacy } from './hooks';
import { ChangePasswordModal } from './ChangePasswordModal';
import AppShortcutIcon from '@mui/icons-material/AppShortcut';
import SmsIcon from '@mui/icons-material/Sms';
import { SetupAppMFAModal } from './SetupAppMFAModal';
import { SetupSmsMFAModal } from './SetupSmsMFAModal';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { StatusBanner } from '@components';
import { MfaMethodCard } from './MfaMethodCard';
import { formatDate } from '@util/date.ts';

interface IPrivacyProps {
  profile: paths[`/profile/{user_id}`]['get']['responses']['200']['content']['application/json'] & {
    id: string;
  };
}

const RemoveMfaWarningBanner = () => (
  <StatusBanner
    variant='warning'
    text={t('profile.privacy.mfaSection.removeMfaModal.bannerWarning')}
  />
);

export const Privacy = ({ profile }: IPrivacyProps) => {
  const {
    isChangePasswordModalOpen,
    openChangePasswordModal,
    closeChangePasswordModal,
    handleChangeMFAMethod,
    hasMfaAppSetup,
    hasMfaSmsSetup,
    isMfaAppSelected,
    isMfaSmsSelected,
    handleDeleteMFAMethod,
    isSetupAppMFAModalOpen,
    openSetupAppMFAModal,
    closeSetupAppMFAModal,
    isSetupSmsMFAModalOpen,
    openSetupSmsMFAModal,
    closeSetupSmsMFAModal,
    isDeleteMfaAppModalOpen,
    handleDeleteMfaAppModalOpen,
    handleDeleteMfaAppModalClose,
    isDeleteMfaSmsModalOpen,
    handleDeleteMfaSmsModalOpen,
    handleDeleteMfaSmsModalClose,
    passwordLastChanged,
  } = usePrivacy(profile);

  const mfaMethodCount = Number(hasMfaAppSetup) + Number(hasMfaSmsSetup);
  const displayRemoveMfaWarning = mfaMethodCount <= 1;

  return (
    <Box
      sx={{
        padding: '1.5rem',
        boxShadow: 'initial',
        backgroundColor: 'white',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', pl: '1rem' }}>
          <Typography variant='h6'>
            {t('profile.privacy.passwordTitle')}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: theme => theme.palette.semiBlack.main }}
          >
            {t('profile.privacy.passwordLastChanged')}:{' '}
            {passwordLastChanged ? formatDate(passwordLastChanged) : 'N/A'}
          </Typography>
        </Box>

        <Button
          type='button'
          variant='outlined'
          color='primary'
          sx={{
            fontSize: '0,875rem',
            fontWeight: 'medium',
            lineHeight: '24px',
            color: 'primary.main',
          }}
          onClick={openChangePasswordModal}
          loadingPosition='center'
        >
          {t('profile.privacy.changePasswordButtonLabel')}
        </Button>
      </Box>

      <Divider sx={{ my: '1rem', width: '100%' }} orientation='horizontal' />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', pl: '1rem' }}>
          <Typography variant='h6'>
            {t('profile.privacy.mfaSection.title')}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: theme => theme.palette.semiBlack.main }}
          >
            {t('profile.privacy.mfaSection.description')}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'stretch',
          width: '100%',
          gap: '0.5rem',
          pt: '1rem',
        }}
      >
        <MfaMethodCard
          title={t('profile.privacy.mfaSection.appTitle')}
          description={
            profile.has_app_mfa
              ? t('profile.privacy.mfaSection.appSetup')
              : t('profile.privacy.mfaSection.appNotSetup')
          }
          isChecked={isMfaAppSelected}
          isSetUp={hasMfaAppSetup}
          onCheck={() => handleChangeMFAMethod(MFAMethod.APP)}
          icon={<AppShortcutIcon />}
          onClick={
            hasMfaAppSetup ? handleDeleteMfaAppModalOpen : openSetupAppMFAModal
          }
        />

        <MfaMethodCard
          title={t('profile.privacy.mfaSection.smsTitle')}
          description={
            profile.has_sms_mfa
              ? profile.mfa_phone_number || ''
              : t('profile.privacy.mfaSection.appNotSetup')
          }
          isChecked={isMfaSmsSelected}
          isSetUp={hasMfaSmsSetup}
          onCheck={() => handleChangeMFAMethod(MFAMethod.SMS)}
          icon={<SmsIcon />}
          onClick={
            hasMfaSmsSetup ? handleDeleteMfaSmsModalOpen : openSetupSmsMFAModal
          }
        />
      </Box>

      {/* BEGIN: modals */}

      <ChangePasswordModal
        onClose={closeChangePasswordModal}
        isOpen={isChangePasswordModalOpen}
      />

      <SetupAppMFAModal
        onClose={closeSetupAppMFAModal}
        isOpen={isSetupAppMFAModalOpen}
      />

      <SetupSmsMFAModal
        onClose={closeSetupSmsMFAModal}
        isOpen={isSetupSmsMFAModalOpen}
      />

      <ConfirmationModal
        title={t('profile.privacy.mfaSection.removeMfaModal.appTitle')}
        banner={displayRemoveMfaWarning && <RemoveMfaWarningBanner />}
        message={t(
          displayRemoveMfaWarning
            ? 'profile.privacy.mfaSection.removeMfaModal.warningDescription'
            : 'profile.privacy.mfaSection.removeMfaModal.appDescription',
        )}
        onConfirm={() => handleDeleteMFAMethod(MFAMethod.APP)}
        onClose={handleDeleteMfaAppModalClose}
        isOpen={isDeleteMfaAppModalOpen}
        sx={{ maxWidth: 600 }}
      />

      <ConfirmationModal
        title={t('profile.privacy.mfaSection.removeMfaModal.smsTitle')}
        banner={displayRemoveMfaWarning && <RemoveMfaWarningBanner />}
        message={t(
          displayRemoveMfaWarning
            ? 'profile.privacy.mfaSection.removeMfaModal.warningDescription'
            : 'profile.privacy.mfaSection.removeMfaModal.smsDescription',
        )}
        onConfirm={() => handleDeleteMFAMethod(MFAMethod.SMS)}
        onClose={handleDeleteMfaSmsModalClose}
        isOpen={isDeleteMfaSmsModalOpen}
        sx={{ maxWidth: 600 }}
      />

      {/* END: modals */}
    </Box>
  );
};
